import * as z from "zod";
import { MAX_NODE_DISK_MIB } from "./node-disk.js";

export const MANAGED_NODE_PROTOCOL = 1;
/** Largest request body the node gateway accepts. Agents size what they send against it. */
export const NODE_GATEWAY_MAX_BODY_BYTES = 10 * 1024 * 1024;
/**
 * Wider limit for result reports only, kept while agents that send whole command output in the
 * result are still running. Current agents stream output and stay within the body limit. Remove
 * once every node runs an agent with output streaming.
 */
export const NODE_GATEWAY_MAX_RESULT_BYTES = 24 * 1024 * 1024;
/** Longest piece of streamed output, in UTF-16 code units, a node sends at once. */
export const NODE_OUTPUT_CHUNK_CHARS = 256 * 1024;
/** Furthest offset, in UTF-16 code units, at which a node operation's output may continue. */
export const NODE_OUTPUT_MAX_OFFSET = 64 * 1024 * 1024;
/** How long a claimed node operation stays with its claimer before it can be claimed again. */
export const NODE_OPERATION_LEASE_MS = 90_000;
const identity = z.string().regex(/^[a-zA-Z0-9_-]{1,100}$/);
const counter = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const WorkspaceLocationSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("local"), homeKey: identity }),
  z.object({
    kind: z.literal("managed-node"),
    nodeId: identity,
    computerId: identity,
    spaceId: identity,
    workspaceGeneration: counter,
    storageId: identity,
  }),
]);
export type WorkspaceLocation = z.infer<typeof WorkspaceLocationSchema>;
export const NodeResourceSchema = z.object({
  nodeId: identity,
  computerId: identity,
  spaceId: identity,
  workspaceGeneration: counter,
  storageId: identity,
  lifecycleEpoch: counter,
});
export type NodeResource = z.infer<typeof NodeResourceSchema>;

/** A negative liveness observation is not proof that an earlier start has ended. */
export const ManagedRuntimeStatusSchema = z
  .object({
    running: z.boolean(),
    phase: z.enum(["absent", "starting", "running", "stopped", "unknown"]),
    runtimeInstanceId: identity.optional(),
  })
  .refine((value) => value.running === (value.phase === "running"), {
    message: "Inconsistent runtime phase",
  });

export const ExecutionPermitSchema = NodeResourceSchema.extend({
  sequence: counter,
  issuedAt: z.number().int().positive(),
  expiresAt: z.number().int().positive(),
  entitlementRevision: counter,
  executionFence: counter,
  executionId: z.string().regex(/^[a-zA-Z0-9_:-]{1,200}$/),
}).strict();
export type ExecutionPermit = z.infer<typeof ExecutionPermitSchema>;
export const SignedExecutionPermitSchema = z
  .object({
    permit: ExecutionPermitSchema,
    signature: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export type SignedExecutionPermit = z.infer<typeof SignedExecutionPermitSchema>;

const resource = NodeResourceSchema.extend({ operationId: identity }).strict();
export const NodeRuntimeRequestSchema = z
  .object({
    action: z.enum([
      "provision",
      "status",
      "exec",
      "browser",
      "screen-mode",
      "terminal",
      "input",
      "observe",
      "actions",
      "files",
      "release-screen",
      "stop",
      "destroy",
    ]),
    method: z.enum(["GET", "POST", "DELETE"]),
    screenId: identity.optional(),
    screenLeaseId: z.string().max(200).optional(),
    cancelRunWork: z.boolean().optional(),
    query: z.string().max(4096).optional(),
    body: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type NodeRuntimeRequest = z.infer<typeof NodeRuntimeRequestSchema>;
const relativePath = z
  .string()
  .min(1)
  .max(1024)
  .refine(
    (path) =>
      !path.startsWith("/") &&
      !path.split("/").some((part) => part === ".." || part === ".") &&
      // biome-ignore lint/suspicious/noControlCharactersInRegex: reject control bytes in node filesystem paths.
      !/[\x00-\x1f\\]/.test(path),
    "A relative workspace path is required",
  );
export const NodeOperationSchema = z.discriminatedUnion("kind", [
  resource.extend({
    kind: z.literal("provision"),
    memoryMib: z.number().int().min(256).max(65536),
    cpuMillis: z.number().int().min(100).max(32000),
    storageMib: z.number().int().min(256).max(1048576),
    maxCpuMillis: z.number().int().min(100).max(32000).optional(),
    maxMemoryMib: z.number().int().min(256).max(65536).optional(),
  }),
  resource.extend({ kind: z.literal("start"), permit: SignedExecutionPermitSchema }),
  resource.extend({ kind: z.literal("stop") }),
  resource.extend({ kind: z.literal("destroy") }),
  resource.extend({ kind: z.literal("status") }),
  resource.extend({
    kind: z.literal("exec"),
    permit: SignedExecutionPermitSchema,
    command: z.string().min(1).max(65536),
    timeoutMs: z.number().int().min(1).max(60000),
  }),
  resource.extend({
    kind: z.literal("read"),
    path: relativePath,
    offset: counter.optional(),
    length: z.number().int().min(1).max(1_048_576).optional(),
  }),
  resource.extend({ kind: z.literal("list"), path: relativePath.optional() }),
  resource.extend({
    kind: z.literal("write"),
    path: relativePath,
    base64: z.string().max(1_398_104),
    offset: counter.optional(),
    truncate: z.boolean().optional(),
  }),
  resource.extend({ kind: z.literal("remove"), path: relativePath }),
  resource.extend({
    kind: z.literal("runtime"),
    request: NodeRuntimeRequestSchema,
    permit: SignedExecutionPermitSchema.optional(),
    execution: z
      .object({ id: z.string().max(200), fence: counter, runtimeInstanceId: identity })
      .optional(),
  }),
]);
export type NodeOperation = z.infer<typeof NodeOperationSchema>;

/** Identity of an old control lease, never an execution authorization. */
export const ScreenControlIdentitySchema = NodeResourceSchema.extend({
  runtimeInstanceId: identity,
  leaseFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  fence: counter,
}).strict();
export type ScreenControlIdentity = z.infer<typeof ScreenControlIdentitySchema>;
export const ControlRevocationProofSchema = ScreenControlIdentitySchema.extend({
  proofVersion: z.literal(1),
  state: z.enum(["revoked", "still-current", "superseded", "unresolved"]),
  attemptSettled: z.boolean(),
  /** Safe to attempt only an idempotent reduction; never proof of release itself. */
  revocationSafe: z.boolean().optional(),
}).strict();
export type ControlRevocationProof = z.infer<typeof ControlRevocationProofSchema>;

/** All callers use the same narrow, versioned reduction-only classification. */
export function isControlRevocation(operation: NodeOperation): boolean {
  if (operation.kind !== "runtime" || !operation.execution) return false;
  const { request, execution } = operation;
  const body = request.body;
  return (
    request.action === "screen-mode" &&
    request.method === "POST" &&
    request.query === undefined &&
    !request.cancelRunWork &&
    body?.interactive === false &&
    body.revokeControl === true &&
    body.revocationVersion === 1 &&
    typeof body.controlToken === "string" &&
    /^[A-Za-z0-9_-]{1,128}$/.test(body.controlToken) &&
    Object.keys(body).every((key) =>
      ["interactive", "revokeControl", "revocationVersion", "controlToken"].includes(key),
    ) &&
    execution.id === body.controlToken &&
    Number.isSafeInteger(execution.fence) &&
    execution.fence >= 0 &&
    identity.safeParse(execution.runtimeInstanceId).success &&
    NodeResourceSchema.safeParse(operation).success
  );
}
export const NodeOperationResultSchema = z.object({
  operationId: identity,
  status: z.enum(["succeeded", "failed", "unknown"]),
  value: z.unknown().optional(),
  error: z.string().max(1000).optional(),
});
export type NodeOperationResult = z.infer<typeof NodeOperationResultSchema>;
/** One piece of an operation's output, at its offset in UTF-16 code units. */
export const NodeOperationOutputSchema = z
  .object({
    connectionId: z.string().uuid(),
    operationId: identity,
    offset: counter.max(NODE_OUTPUT_MAX_OFFSET),
    data: z.string().min(1).max(NODE_OUTPUT_CHUNK_CHARS),
  })
  .strict();
export type NodeOperationOutput = z.infer<typeof NodeOperationOutputSchema>;
export const NodeRuntimeEventSchema = NodeResourceSchema.extend({
  eventSeq: counter,
  runtimeInstanceId: identity,
  kind: z.enum(["starting", "ready", "stopped", "purged"]),
  occurredAt: z.string().datetime(),
}).strict();
export type NodeRuntimeEvent = z.infer<typeof NodeRuntimeEventSchema>;
export const ComputeNodeSchema = z.object({
  id: identity,
  name: z.string(),
  address: z.string(),
  state: z.enum(["enrolling", "ready", "suspect", "offline", "draining", "quarantined"]),
  heartbeatAt: z.string().datetime().nullable(),
  cpuMillis: counter,
  memoryMib: counter,
  storageMib: counter,
  reservedCpuMillis: counter,
  reservedMemoryMib: counter,
  reservedStorageMib: counter,
  // Reservations can temporarily deduct more RAM than was reported; the next beat replaces it.
  memoryAvailableMib: z
    .number()
    .int()
    .min(Number.MIN_SAFE_INTEGER)
    .max(Number.MAX_SAFE_INTEGER)
    .nullable()
    .default(null),
  swapFreeMib: counter.nullable().default(null),
  diskTotalMib: z.number().int().positive().max(MAX_NODE_DISK_MIB).nullable().default(null),
  diskAvailableMib: z.number().int().nonnegative().max(MAX_NODE_DISK_MIB).nullable().default(null),
  diskPressure: z.enum(["normal", "hold", "evacuate"]).default("normal"),
  storageOversellPercent: z.number().int().min(100).max(300).default(100),
  protocol: counter,
  isolationVerified: z.boolean(),
  bootstrapStep: z.string(),
  bootstrapError: z.string().nullable(),
});
export const NodeEnrollmentInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    address: z
      .string()
      .trim()
      .min(1)
      .max(253)
      .regex(/^[A-Za-z0-9.:_-]+$/),
    port: z.number().int().min(1).max(65535).default(22),
    username: z
      .string()
      .regex(/^[a-z_][a-z0-9_-]{0,31}$/)
      .default("root"),
    hostKey: z.string().regex(/^SHA256:[A-Za-z0-9+/]{43}=?$/),
    credential: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("private-key"), value: z.string().min(20).max(32768) }),
      z.object({ kind: z.literal("password"), value: z.string().min(1).max(1024) }),
    ]),
  })
  .strict();
